import {
  Directive,
  input,
  output,
  OutputEmitterRef,
  inject,
  effect,
  untracked,
  InputSignal,
  ElementRef,
  TemplateRef
} from '@angular/core';

import { from, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { KeyCode } from '../../domain/key-code.enum';
import { WINDOW_TOKEN } from '../../services/bootstrap.service';
import { LinkRegistryService } from '../../services/link-registry.service';
import { ButtonComponent } from '../../components/button/button.component';
import { DataAnalyticsService } from '../../services/data-analytics/data-analytics.service';
import { RouterBaseService, StateDeclaration } from '../../services/router/router-base.service';

import type { Observable, ObservableInput } from 'rxjs';

// ReferenceType distinguishes between link-based navigation and state-based navigation
enum ReferenceType {
  LINK,
  STATE
}
// Event interface for async navigation output
export interface HrefNavigationEvent {
  ref: string;
  params?: HrefParams;
  options?: unknown;
  navigate(ref?: string, params?: HrefParams, options?: unknown): Observable<void>;
}

// Params for navigation can be any key-value pairs
export interface HrefParams {
  [key: string]: unknown | Array<unknown>;
}

// Internal descriptor for navigation context
interface Descriptor {
  ref: string;
  refType: ReferenceType;
  params: HrefParams;
  options: unknown;
  async: boolean;
}

/**
 * This directive's behaviour is similar to href, but it also supports state names and link ids from the link registry as a parameter.
 */

/**
 * HrefDirective
 * Provides navigation via href, state names, or link registry ids.
 * - Supports signals for href, params, options, async navigation, and confirmation templates.
 * - Emits navigation events for async mode.
 * - Handles role and target attributes for accessibility and browser behaviour.
 */
@Directive({
  selector: '[tdWbHref]',
  // Host bindings for href, role, and event handlers
  host : {
    '[attr.href]': 'hrefAttr',
    '[attr.role]': 'roleAttr',
    '(keydown)': 'onKeyDown($event)',
    '(click)': 'onClick($event)'
  }
})
export class HrefDirective {
  private static readonly CURRENT_STATE_PLACEHOLDER: string = '$current';

  //Signals for directive inputs
  href: InputSignal<string> = input('', { alias: 'tdWbHref' });
  hrefParams: InputSignal<HrefParams | undefined> = input( undefined as HrefParams | undefined, { alias: 'tdWbHrefParams' });
  hrefOptions: InputSignal<unknown> = input<unknown>(undefined, { alias: 'tdWbHrefOptions' });
  tdWbHrefTaggingEvent: InputSignal<string> = input('', { alias: 'tdWbHrefTaggingEvent' });
  tdWbHrefTaggingType: InputSignal<string> = input('link', { alias: 'tdWbHrefTaggingType' });
  confirmTemplate: InputSignal<TemplateRef<unknown> | null> = input<TemplateRef<unknown> | null>(null, { alias: 'confirmTemplate' });
  asyncNavigate: InputSignal<boolean> = input(false, { alias: 'tdWbHrefAsync' });

  readonly onNavigate: OutputEmitterRef<HrefNavigationEvent> = output<HrefNavigationEvent>();

  hrefAttr: string = '';
  roleAttr: string | null = null;

  private descriptor!: Descriptor;

  // Injected services for navigation, analytics, and DOM access
  private readonly linkRegistryService: LinkRegistryService = inject(LinkRegistryService);
  private readonly routerService: RouterBaseService = inject(RouterBaseService);
  private readonly button: ButtonComponent | null = inject(ButtonComponent, { optional: true, host: true });
  private readonly browserWindow: Window = inject(WINDOW_TOKEN);
  private readonly dataAnalyticsService: DataAnalyticsService = inject(DataAnalyticsService);
  private readonly el: ElementRef<HTMLElement> = inject<ElementRef<HTMLElement>>(ElementRef);

  private get roleAttrFromHost(): string | null {
    return this.el.nativeElement.getAttribute('role');
  }

  private get targetAttrFromHost(): string | null {
    return this.el.nativeElement.getAttribute('target');
  }

  /**
   * Reactive effect: updates navigation descriptor and href/role attributes when inputs change.
   * - If href is falsy, disables navigation and sets hrefAttr to '' (spec: disables service calls, sets hrefAttr to '').
   * - Otherwise, builds navigation descriptor and updates href/role attributes.
   * - Spec tests: service call behaviour, hrefAttr/roleAttr assignment, async navigation.
   */
  constructor() {
    effect(() => {
      const href: string = this.href();
      const params: HrefParams | undefined = this.hrefParams();
      const options: unknown = this.hrefOptions();
      const confirm = this.confirmTemplate() != null;
      if (!href) {
        untracked(() => {
          this.hrefAttr = '';
          if (this.button === null) {
            this.roleAttr = this.roleAttrFromHost ?? 'button';
          } else {
            this.roleAttr = null;
          }
        });
        return;
      }
      const next = this.getDescriptor(href, params, options, confirm);
      untracked(() => {
        this.descriptor = next;
        this.updateHref(this.descriptor);
      });
    });
  }

  onKeyDown(event: KeyboardEvent): void {
    const keyCode = event.key;
    if (((this.roleAttr === 'button' || this.button !== null) && (keyCode === KeyCode.ENTER || keyCode === KeyCode.SPACE)) ||
        (this.roleAttr === 'link' && keyCode === KeyCode.ENTER)) {
      this.click(event);
    }
  }

  onClick(event: MouseEvent): void {
    this.click(event);
  }

  private click(event: UIEvent): void {
    if (this.tdWbHrefTaggingEvent){
      this.dataAnalyticsService.emitLinkClickEvent(this.tdWbHrefTaggingEvent(), this.tdWbHrefTaggingType());
    }
    event.preventDefault();
    this.navigate();
  }

  // If asyncNavigate is true, emits navigation event for external handling. Otherwise, performs navigation directly.
  private navigate(): void {
    if (this.asyncNavigate()) {
      const event: HrefNavigationEvent = {
        ref: this.descriptor.ref,
        navigate: (ref?: string, params?: HrefParams, options?: unknown): Observable<void> => {
          const descriptor = ref === undefined ? this.descriptor : this.getDescriptor(ref, params, options, this.asyncNavigate());
          return this.doNavigate(descriptor);
        }
      };
      if (this.descriptor.params !== undefined) {
        event.params = this.descriptor.params;
      }
      if (this.descriptor.options !== undefined) {
        event.options = this.descriptor.options;
      }
      this.onNavigate.emit(event);
    } else {
      this.doNavigate(this.descriptor);
    }
  }

  // For LINK type, resolves href and navigates via window.location or window.open. For STATE type, navigated via routerService.go
  private doNavigate(descriptor: Descriptor): Observable<void> {
    if (descriptor.refType === ReferenceType.LINK) {
      const href = this.getHref(descriptor);
      if (this.targetAttrFromHost === null) {
        // Based on existence of href strategy we will know who to call
        this.browserWindow.location.assign(href);
      } else {
        this.browserWindow.open(href, this.targetAttrFromHost);
      }
      return of();
    } else {
      const stateName = descriptor.ref === HrefDirective.CURRENT_STATE_PLACEHOLDER ? this.routerService.getGlobalsCurrent() : descriptor.ref;
      return from(this.routerService.go(stateName, descriptor.params, descriptor.options) as ObservableInput<unknown>).pipe(map((): void => {
      }));
    }
  }

  //Sets hrefAttr and roleAttr based on descriptor.
  private updateHref(descriptor: Descriptor): void {
    if (this.isHref(descriptor)) {
      this.hrefAttr = this.getHref(descriptor);
      if (this.button == null) {
        this.roleAttr = this.roleAttrFromHost ?? 'link';
      } else {
        this.roleAttr = null;
      }
    } else {
      this.hrefAttr = '';
      if (this.button == null) {
        this.roleAttr = this.roleAttrFromHost ?? 'button';
      } else {
        this.roleAttr = null;
      }
    }
  }

  //Determines navigation type (LINK or STATE) based on ref and registry lookups.
  private getDescriptor(ref: string, params?: HrefParams, options?: unknown, confirm?: boolean): Descriptor {
    const result = {
      ref,
      params,
      options,
      async: confirm === true,
      refType: ReferenceType.LINK
    } as Descriptor;
    let stateDefinition: StateDeclaration;

    if (result.ref === HrefDirective.CURRENT_STATE_PLACEHOLDER) {
      result.refType = ReferenceType.STATE;
    } else if (this.linkRegistryService.getLink(ref, undefined, true) === undefined) {
      stateDefinition = this.routerService.getStateRegistry(ref);
      if ((stateDefinition !== null && stateDefinition)) {
        result.refType = ReferenceType.STATE;
      }
    }
    return result;
  }

  private isHref(descriptor: Descriptor): boolean {
    return descriptor.refType === ReferenceType.LINK && !descriptor.async;
  }

  private getHref(descriptor: Descriptor): string {
    let href = this.linkRegistryService.getLink(descriptor.ref, descriptor.params, true);
    if (href === undefined) {
      if (descriptor.params !== undefined) {
        href = this.linkRegistryService.addParameters(descriptor.ref, descriptor.params);
      } else {
        href = descriptor.ref;
      }
    }
    return href;
  }
}
